import {
  EuiPopover,
  EuiButton,
  EuiThemeProvider,
  EuiBadge,
  EuiIcon,
  EuiPanel,
  EuiFlexGroup,
  EuiFlexItem,
  EuiDescriptionList,
  EuiText,
} from "@inscopix/ideas-eui";
import { Tooltip } from "components/Tooltip/Tooltip";
import { TourKey, useTourContext } from "providers/TourProvider/TourProvider";
import { useMemo, useState } from "react";
import { ReactComponent as circle } from "assets/circleIcon.svg";
import { useTourStep } from "providers/TourProvider/TourProvider.hooks";

export const HeaderTours = () => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const completedTours = useTourContext((s) => s.completedTours);
  const startTour = useTourContext((s) => s.startTour);
  const getTourOptionsForPage = useTourContext((s) => s.getTourOptionsForPage);
  const completeTour = useTourContext((s) => s.completeTour);
  const setTourIncomplete = useTourContext((s) => s.setTourIncomplete);
  const currentPage = useTourContext((s) => s.currentPage);
  const tours = getTourOptionsForPage(currentPage);
  const tourKeys = Object.keys(tours) as TourKey[];

  const handleClick = () => {
    setIsPopoverOpen((prev) => !prev);
  };

  const { component: NewVersionIntro_25_2_step0, anchorId } = useTourStep(
    "TourNewVersionIntro_25_2",
    0,
  );
  const { component: NewVersionIntro_25_2_step1 } = useTourStep(
    "TourNewVersionIntro_25_2",
    1,
  );
  const { component: NewVersionIntro_25_2_step2 } = useTourStep(
    "TourNewVersionIntro_25_2",
    2,
  );
  const { component: NewVersionIntro_25_2_step3 } = useTourStep(
    "TourNewVersionIntro_25_2",
    3,
  );
  const { component: NewVersionIntro_25_2_step4 } = useTourStep(
    "TourNewVersionIntro_25_2",
    4,
  );
  const { component: NewVersionIntro_25_2_step5 } = useTourStep(
    "TourNewVersionIntro_25_2",
    5,
  );
  const { component: NewVersionIntro_25_2_step6 } = useTourStep(
    "TourNewVersionIntro_25_2",
    6,
  );
  const tourSteps = [
    NewVersionIntro_25_2_step0,
    NewVersionIntro_25_2_step1,
    NewVersionIntro_25_2_step2,
    NewVersionIntro_25_2_step3,
    NewVersionIntro_25_2_step4,
    NewVersionIntro_25_2_step5,
    NewVersionIntro_25_2_step6,
  ];

  const toursPanels = tourKeys.map((key) => {
    const tour = tours[key];
    const completed = completedTours.includes(key);
    return (
      <EuiPanel
        key={key}
        paddingSize="s"
        color={completed ? "subdued" : "transparent"}
        hasBorder={true}
        hasShadow={false}
        onClick={() => {
          startTour(key);
          setIsPopoverOpen(false);
        }}
        style={{ cursor: "pointer", marginBottom: "10px" }}
      >
        <EuiFlexGroup gutterSize="s" alignItems="center">
          <EuiFlexItem grow={false}>
            <EuiIcon type="playFilled" color={completed ? "text" : "primary"} />
          </EuiFlexItem>
          <EuiFlexItem>
            <EuiDescriptionList
              compressed
              listItems={[
                {
                  title: tour.name,
                  description: tour.description,
                },
              ]}
            />
          </EuiFlexItem>
          <EuiFlexItem grow={false}>
            {completed ? (
              <Tooltip content="Mark tour as incomplete">
                <EuiIcon
                  type="checkInCircleFilled"
                  color="success"
                  onClick={(e) => {
                    e.stopPropagation();
                    void setTourIncomplete(key);
                  }}
                />
              </Tooltip>
            ) : (
              <Tooltip content="Mark tour as completed">
                <EuiIcon
                  type={circle}
                  color="text"
                  onClick={(e) => {
                    e.stopPropagation();
                    void completeTour(key);
                  }}
                />
              </Tooltip>
            )}
          </EuiFlexItem>
        </EuiFlexGroup>
      </EuiPanel>
    );
  });

  const hasIncompleteTours = tourKeys.some(
    (key) => !completedTours.includes(key),
  );

  const button = useMemo(
    () => (
      <EuiThemeProvider colorMode="dark" wrapperProps={{ cloneElement: true }}>
        <EuiButton
          size="s"
          color="primary"
          minWidth={0}
          onClick={() => setIsPopoverOpen((prev) => !prev)}
          id={anchorId}
        >
          Guides{" "}
          {hasIncompleteTours && (
            <EuiBadge color="primary">
              {
                Object.keys(tours).filter(
                  (key) => !completedTours.includes(key as TourKey),
                ).length
              }
            </EuiBadge>
          )}
        </EuiButton>
      </EuiThemeProvider>
    ),
    [anchorId, completedTours, hasIncompleteTours, tours],
  );

  if (currentPage === null || Object.keys(tours).length === 0) {
    return null;
  }

  return (
    <>
      <EuiPopover
        isOpen={isPopoverOpen}
        closePopover={handleClick}
        anchorPosition="downRight"
        panelPaddingSize="s"
        button={button}
      >
        <EuiPanel paddingSize="s" color="transparent">
          <EuiText>
            <p>
              Select a guide below to start a tour of IDEAS features on this
              page
            </p>
          </EuiText>
        </EuiPanel>
        {toursPanels}
      </EuiPopover>
      {tourSteps}
    </>
  );
};
